import { Injectable, inject } from '@angular/core';
import { ValidationIssue } from '../types/validation';
import { TranslocoService } from '@jsverse/transloco';

@Injectable({
  providedIn: 'root'
})
export class ValidationTranslatorService {
  private translocoService = inject(TranslocoService);

  /**
   * Translate validation code to human-readable message
   */
  translateIssue(issue: ValidationIssue): string {
    const { code, context } = issue;

    // Use transloco to get localized validation messages
    const translationKey = `validation.${code}`;

    // For messages with parameters, pass the context
    if (context && Object.keys(context).length > 0) {
      return this.translocoService.translate(translationKey, context);
    }

    // For simple messages without parameters
    const translated = this.translocoService.translate(translationKey);

    // Fallback if translation is missing
    if (translated === translationKey) {
      return `Validation error: ${code}`;
    }

    return translated;
  }

  /**
   * Get summary of issues for display
   */
  getSummary(issues: ValidationIssue[]): string {
    const errors = issues.filter(i => i.severity === 'error').length;
    const warnings = issues.filter(i => i.severity === 'warning').length;

    if (errors > 0 && warnings > 0) {
      const total = errors + warnings;
      return this.translocoService.translate('validation.summary.issuesDetected', { count: total });
    } else if (errors > 0) {
      if (errors === 1) {
        return this.translocoService.translate('validation.summary.errorDetected');
      } else {
        return this.translocoService.translate('validation.summary.errorsDetected', { count: errors });
      }
    } else if (warnings > 0) {
      if (warnings === 1) {
        return this.translocoService.translate('validation.summary.warning');
      } else {
        return this.translocoService.translate('validation.summary.warnings', { count: warnings });
      }
    }

    return this.translocoService.translate('validation.summary.allGood');
  }

  /**
   * Get error count text with proper pluralization
   */
  getErrorCountText(count: number): string {
    if (count === 1) {
      return this.translocoService.translate('validation.summary.errorDetected');
    }
    return this.translocoService.translate('validation.summary.errorsDetected', { count });
  }

  /**
   * Get warning count text with proper pluralization
   */
  getWarningCountText(count: number): string {
    if (count === 1) {
      return this.translocoService.translate('validation.summary.warning');
    }
    return this.translocoService.translate('validation.summary.warnings', { count });
  }
}